function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { ButtonBase } from './ButtonBase';
import createTypography from '../utils/createTypography';
const buttonStylesMap = {
  loading: {
    base: 'sui-inline-flex sui-items-center !sui-text-transparent sui-pointer-events-none',
    active: {
      primary: '!sui-bg-button-primary',
      secondary: '!sui-bg-button-secondary',
      tertiary: 'sui-border-button-medium'
    },
    inactive: {
      secondary: '!sui-bg-button-secondary sui-border-button-inactive'
    }
  }
};
const buttonStyles = {
  primary: _ref => {
    let {
      fullWidth,
      typography,
      disabled
    } = _ref;
    return classNames('sui-btn sui-btn-primary sui-btn-primary-focus', {
      'sui-w-full': fullWidth,
      'sui-w-full sm:sui-w-fit': !fullWidth,
      'sui-btn-primary-active sui-btn-primary-hover': !disabled,
      'sui-btn-primary-inactive': disabled
    }, typography);
  },
  secondary: _ref2 => {
    let {
      disabled,
      fullWidth,
      typography
    } = _ref2;
    return classNames('sui-btn sui-btn-secondary sui-btn-secondary-focus', {
      'sui-w-full': fullWidth,
      'sui-w-full sm:sui-w-fit': !fullWidth,
      'sui-btn-secondary-active sui-btn-secondary-hover': !disabled,
      'sui-btn-secondary-inactive': disabled
    }, typography);
  },
  tertiary: _ref3 => {
    let {
      disabled,
      fullWidth,
      typography
    } = _ref3;
    return classNames('sui-btn sui-btn-tertiary sui-btn-tertiary-focus', {
      'sui-w-full': fullWidth,
      'sui-w-full sm:sui-w-fit': !fullWidth,
      'sui-btn-tertiary-hover sui-btn-tertiary-active': !disabled,
      'sui-btn-tertiary-inactive': disabled
    }, typography);
  },
  text: _ref4 => {
    let {
      disabled,
      typography
    } = _ref4;
    return classNames('sui-btn sui-btn-text sui-btn-text-focus', {
      'sui-btn-text-active sui-btn-text-hover': !disabled,
      'sui-btn-text-inactive': disabled
    }, typography);
  },
  ghost: _ref5 => {
    let {
      disabled,
      typography
    } = _ref5;
    return classNames('sui-btn sui-btn-ghost sui-btn-ghost-focus sui-rounded-base', {
      'sui-btn-ghost-active sui-btn-ghost-hover': !disabled,
      'sui-btn-ghost-inactive': disabled
    }, typography);
  }
};
const loadingButtonStyles = {
  primary: _ref6 => {
    let {
      buttonClasses,
      disabled
    } = _ref6;
    return classNames(`${buttonClasses} ${buttonStylesMap.loading.base}`, {
      [`${buttonStylesMap.loading.active.primary}`]: !disabled
    });
  },
  secondary: _ref7 => {
    let {
      buttonClasses,
      disabled
    } = _ref7;
    return classNames(`${buttonClasses} ${buttonStylesMap.loading.base}`, {
      [`${buttonStylesMap.loading.active.secondary}`]: !disabled,
      [`${buttonStylesMap.loading.inactive.secondary}`]: disabled
    });
  },
  tertiary: _ref8 => {
    let {
      buttonClasses,
      disabled
    } = _ref8;
    return classNames(`${buttonClasses} ${buttonStylesMap.loading.base}`);
  },
  text: _ref9 => {
    let {
      buttonClasses
    } = _ref9;
    return classNames(`${buttonClasses} ${buttonStylesMap.loading.base}`);
  },
  ghost: _ref10 => {
    let {
      buttonClasses
    } = _ref10;
    return classNames(`${buttonClasses} ${buttonStylesMap.loading.base}`);
  }
};

/**
 * The `Button` component comes with four variants: primary (default), secondary, text, and ghost.
 *
 * Related components: [ButtonGroup](#buttongroup), [IconButton](#iconbutton), [LoadingButton](#loadingbutton)
 *
 *
 * Usage:
 *
 * ```jsx
 * import { Button } from '@one-thd/sui-atomic-components';
 * ```
 */
const Button = /*#__PURE__*/React.forwardRef((_ref11, ref) => {
  let {
    children,
    endIcon: EndIconComponent,
    startIcon: StartIconComponent,
    disabled = false,
    fullWidth = false,
    href,
    variant: variantProp,
    loading,
    ...rest
  } = _ref11;
  const variant = buttonStyles[variantProp] ? variantProp : 'primary';
  const isPrimary = variant === 'primary' || !variant;
  const isRegularWeight = variant === 'text' || variant === 'tertiary' || variant === 'ghost';
  const typography = createTypography({
    variant: 'body-base',
    weight: isRegularWeight ? 'regular' : 'bold',
    height: variant === 'tertiary' ? 'tight' : 'normal'
  });
  const buttonClasses = buttonStyles[isPrimary ? 'primary' : variant]({
    disabled,
    fullWidth,
    typography: typography.classes
  });
  const classes = loading ? loadingButtonStyles[isPrimary ? 'primary' : variant]({
    buttonClasses,
    disabled
  }) : buttonClasses;
  const startIcon = StartIconComponent && /*#__PURE__*/React.createElement("span", {
    className: "sui-mr-2 sui--ml-1",
    style: {
      display: 'inherit'
    }
  }, /*#__PURE__*/React.createElement(StartIconComponent, {
    size: "small"
  }));
  const endIcon = EndIconComponent && /*#__PURE__*/React.createElement("span", {
    className: "sui-ml-2 sui--mr-1",
    style: {
      display: 'inherit'
    }
  }, /*#__PURE__*/React.createElement(EndIconComponent, {
    size: "small"
  }));
  return /*#__PURE__*/React.createElement(React.Fragment, null, href && /*#__PURE__*/React.createElement("a", _extends({
    href: href,
    className: classes,
    ref: ref
  }, rest), startIcon, children, endIcon), !href && /*#__PURE__*/React.createElement(ButtonBase, _extends({
    disabled: disabled || loading,
    ref: ref,
    unstyled: true,
    className: classes
  }, rest), startIcon, children, endIcon));
});
Button.displayName = 'Button';
Button.propTypes = {
  /**
   * @ignore
   * Class override is for internal purposes only, will be removed shortly.
   * Use at your own risk.
   */
  children: PropTypes.node,
  /**
   * If `true`, the component is disabled.
   */
  disabled: PropTypes.bool,
  /**
   * An icon to display after the text inside the button. Needs to be a React React element type (ie. MyComponent)
   * Needs to be a React element type (ie. MyIcon), not an instance (ie. <MyIcon />)
   */
  endIcon: PropTypes.elementType,
  /**
   * If `true`, the button will take up the full width of its container.
   */
  fullWidth: PropTypes.bool,
  /**
   * The URL to link to when the button is clicked.
   * If defined, an `a` element will be used as the root node.
   */
  href: PropTypes.string,
  /**
   * @ignore
   * If true applies the appropriate classes to convert it into a LoadingButton, for internal use only
   */
  loading: PropTypes.bool,
  /**
   * Callback fired when the component is clicked.
   */
  onClick: PropTypes.func,
  /**
   * An icon to display before the text inside the button.
   * Needs to be a React element type (ie. MyIcon), not an instance (ie. <MyIcon />)
   */
  startIcon: PropTypes.elementType,
  /**
   * The variant to use.
   * @default 'primary'
   */
  variant: PropTypes.oneOf(['primary', 'secondary', 'tertiary', 'text', 'ghost'])
};
export { Button };
//# sourceMappingURL=Button.js.map